// PE-6337 — AES-256-GCM transport encryption (hex wire: IV || ciphertext || tag)
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { resolveAesGcmKey } from '../utils/secret-value-unwrap.util';

/**
 * PE-6337 — Patient Portal transport crypto via Web Crypto AES-256-GCM.
 * Wire format (match BE): lowercase hex of IV(12) || ciphertext || authTag(16).
 * Key: environment.encryption.key must be outer Base64(UTF-8(plainAesKey)).
 * Plain AES key strings are rejected.
 */
@Injectable({
  providedIn: 'root'
})
export class EncryptionService {
  private readonly keyBase64: string;
  private keyPromise: Promise<CryptoKey> | null = null;

  private static readonly IV_LENGTH = 12;
  private static readonly TAG_LENGTH = 16;

  constructor() {
    const raw = environment.encryption?.key?.trim() ?? '';
    if (!raw || raw === 'test') {
      throw new Error(
        'PE-6337: environment.encryption.key must be outer Base64(UTF-8(aesKey)), not "test" or empty'
      );
    }
    // PE-6337: require outer Base64; plain AES key → error
    const resolved = resolveAesGcmKey(raw);
    if (!resolved) {
      throw new Error(
        'PE-6337: environment.encryption.key must be outer Base64(UTF-8(aesKey)); plain AES key is not accepted'
      );
    }
    this.keyBase64 = resolved;
  }

  // PE-6337 — encrypt plaintext → lowercase hex (IV || ciphertext || tag)
  async encryptAes256Gcm(data: string): Promise<string> {
    if (data == null) {
      return '';
    }
    const key = await this.getCryptoKey();
    const iv = crypto.getRandomValues(new Uint8Array(EncryptionService.IV_LENGTH));
    const encoded = new TextEncoder().encode(data);
    const ivCopy = new Uint8Array(iv);
    const cipherBuf = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: ivCopy, tagLength: EncryptionService.TAG_LENGTH * 8 },
      key,
      encoded
    );
    const combined = new Uint8Array(iv.length + cipherBuf.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(cipherBuf), iv.length);
    return this.bytesToHex(combined);
  }

  // PE-6337 — decrypt lowercase hex (IV || ciphertext || tag) → plaintext
  async decryptAes256Gcm(encryptedHex: string): Promise<string> {
    if (!encryptedHex || !encryptedHex.trim()) {
      return '';
    }
    const hex = encryptedHex.trim().toLowerCase();
    const minLen = (EncryptionService.IV_LENGTH + EncryptionService.TAG_LENGTH) * 2;
    if (hex.length < minLen || hex.length % 2 !== 0 || !/^[0-9a-f]+$/.test(hex)) {
      return '';
    }
    try {
      const bytes = this.hexToBytes(hex);
      const iv = new Uint8Array(bytes.subarray(0, EncryptionService.IV_LENGTH));
      const ciphertextWithTag = bytes.subarray(EncryptionService.IV_LENGTH);
      const key = await this.getCryptoKey();
      // Copy into a new ArrayBuffer-backed Uint8Array (BufferSource typing)
      const cipherCopy = new Uint8Array(ciphertextWithTag);
      const plainBuf = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv, tagLength: EncryptionService.TAG_LENGTH * 8 },
        key,
        cipherCopy
      );
      return new TextDecoder().decode(plainBuf);
    } catch {
      return '';
    }
  }

  private getCryptoKey(): Promise<CryptoKey> {
    if (!this.keyPromise) {
      this.keyPromise = this.importKey();
    }
    return this.keyPromise;
  }

  private async importKey(): Promise<CryptoKey> {
    const raw = Uint8Array.from(atob(this.keyBase64), (c) => c.charCodeAt(0));
    if (raw.length !== 32) {
      throw new Error('PE-6337: AES key must decode to exactly 32 bytes');
    }
    // Copy so BufferSource is a plain ArrayBuffer-backed view
    const keyBytes = new Uint8Array(raw);
    return crypto.subtle.importKey('raw', keyBytes, { name: 'AES-GCM' }, false, [
      'encrypt',
      'decrypt'
    ]);
  }

  private bytesToHex(bytes: Uint8Array): string {
    let out = '';
    for (let i = 0; i < bytes.length; i++) {
      out += bytes[i].toString(16).padStart(2, '0');
    }
    return out;
  }

  private hexToBytes(hex: string): Uint8Array {
    const out = new Uint8Array(hex.length / 2);
    for (let i = 0; i < out.length; i++) {
      out[i] = parseInt(hex.substr(i * 2, 2), 16);
    }
    return out;
  }
}
